<template>
  <div class="ds-dropdown" data-testid="ds-dropdown">
    <Menu>
      <OnClickOutside @trigger="close">
        <Float
          as="div"
          :offset="10"
          :shift="6"
          :flip="10"
          :placement="placement"
          :z-index="zIndex"
          :show="show"
          :portal="portal"
          enter="ds-dropdown-enter"
          enter-from="ds-dropdown-enter-from"
          enter-to="ds-dropdown-enter-to"
          leave="ds-dropdown-leave"
          leave-from="ds-dropdown-leave-from"
          leave-to="ds-dropdown-leave-to"
          tailwindcss-origin-class
        >
          <MenuButton
            type="button"
            class="ds-dropdown-button"
            :data-testid="dataTestid"
            @click.stop="toggle"
            @mouseleave="onMouseLeave"
          >
            <slot name="button" />
          </MenuButton>
          <MenuItems
            :class="['ds-dropdown-items', `ds-dropdown-${size}`]"
            static
            data-testid="ds-dropdown-items"
            @mouseenter="open"
            @mouseleave="onMouseLeave"
          >
            <MenuItem v-for="(item, key) in items" :key="key">
              <div v-if="item.route" class="group">
                <router-link
                  :data-testid="item.dataTestid"
                  :to="item.route"
                  :class="[
                    'ds-dropdown-item',
                    item.cssClasses,
                    { 'ds-dropdown-item-active': isActive(item) },
                  ]"
                  :target="`_${getTarget(item)}`"
                  @click="close"
                >
                  <DsComponentRender
                    v-if="item.icon"
                    :component="item.icon"
                    :class="{ 'ds-dropdown-icon': !hasIconClass(item.icon) }"
                  />
                  {{ item.label }}
                  <DsComponentRender
                    v-if="item.suffixIcon"
                    :component="item.suffixIcon"
                    :class="{
                      'ds-dropdown-icon ml-auto !mr-0': !hasIconClass(
                        item.suffixIcon
                      ),
                    }"
                  />
                </router-link>
              </div>
              <button
                v-else
                type="button"
                :data-testid="item.dataTestid"
                :class="[
                  'ds-dropdown-item',
                  item.cssClasses,
                  {
                    'ds-dropdown-item-active': isActive(item),
                  },
                ]"
                @click.stop="callFunction(item)"
              >
                <DsComponentRender
                  v-if="item.icon"
                  :component="item.icon"
                  :class="{ 'ds-dropdown-icon': !hasIconClass(item.icon) }"
                />
                <template v-if="isLabelString(item.label)">
                  {{ item.label }}
                </template>
                <DsComponentRender
                  v-else
                  :component="item.label as DsComponentRenderProp"
                />
                <DsComponentRender
                  v-if="item.suffixIcon"
                  :component="item.suffixIcon"
                  :class="{
                    'ds-dropdown-icon ml-auto !mr-0': !hasIconClass(
                      item.suffixIcon
                    ),
                  }"
                />
              </button>
            </MenuItem>
          </MenuItems>
        </Float>
      </OnClickOutside>
    </Menu>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import {
  DsComponentRender,
  type DsComponentRenderProp,
} from "../component-render";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import { Float } from "@headlessui-float/vue";
import { OnClickOutside } from "@vueuse/components";
import type { PropType } from "vue";
import { useLink } from "vue-router";

import type {
  DsDropdownItem,
  DsPlacement,
  DsThemeSize,
  TargetType,
} from "../../types";

defineOptions({
  name: "DsDropdown",
});

const emit = defineEmits<{
  opened: [boolean];
  closed: [boolean];
}>();

const props = defineProps({
  placement: {
    type: String as PropType<DsPlacement>,
    default: "bottom-end",
  },
  items: {
    type: Array as PropType<DsDropdownItem[]>,
    required: true,
  },
  size: {
    type: String as PropType<DsThemeSize>,
    default: "md",
  },
  dataTestid: {
    type: String,
    default: "ds-dropdown-button",
  },
  delay: {
    type: Number,
    default: 250,
  },
  closeOnMouseLeave: {
    type: Boolean,
    default: false,
  },
  portal: {
    type: Boolean,
    default: false,
  },
  zIndex: {
    type: Number,
    default: 10,
  },
});

const show = ref(false);
const openTimer = ref(0);
const closeTimer = ref(0);

function toggle(): void {
  show.value = !show.value;
}
function open(): void {
  if (closeTimer.value) {
    clearTimeout(closeTimer.value);
  }
  openTimer.value = window.setTimeout(() => {
    show.value = true;
  }, props.delay);
}
function close(): void {
  if (openTimer.value) {
    clearTimeout(openTimer.value);
  }

  closeTimer.value = window.setTimeout(
    () => {
      show.value = false;
    },
    props.closeOnMouseLeave ? props.delay : 0
  );
}

function isActive(item: DsDropdownItem): boolean {
  if (item.active !== undefined) {
    return item.active;
  }

  if (item.route) {
    const { isExactActive } = useLink({ to: item.route });

    return isExactActive.value;
  }

  return false;
}

function callFunction(item: DsDropdownItem): void {
  close();
  if (item.action && typeof item.action === "function") {
    item.action();
  }
}
function hasIconClass(iconComponent: DsComponentRenderProp): boolean {
  return (
    !!iconComponent.attributes &&
    Reflect.has(iconComponent.attributes as object, "class")
  );
}
function getTarget(item: DsDropdownItem): TargetType {
  return item.target !== undefined ? item.target : "self";
}
function onMouseLeave(): void {
  if (props.closeOnMouseLeave) {
    close();
  }
}
function isLabelString(label: string | DsComponentRenderProp): boolean {
  return typeof label === "string";
}

watch(show, (newValue) => {
  if (newValue) {
    emit("opened", newValue);
  } else {
    emit("closed", newValue);
  }
});
</script>

<style lang="scss" scoped>
.ds-dropdown {
  &-button {
    @apply flex justify-center items-center;
  }

  &-items {
    @apply rounded-lg shadow-lg focus:outline-0 ring-1 ring-black bg-white ring-opacity-5 overflow-hidden -mt-1 py-2;

    &.ds-dropdown-xxs {
      @apply w-20;
    }

    &.ds-dropdown-xs {
      @apply w-24;
    }

    &.ds-dropdown-sm {
      @apply w-36;
    }

    &.ds-dropdown-md {
      @apply w-52;
    }

    &.ds-dropdown-lg {
      @apply w-60;
    }

    &.ds-dropdown-xl {
      @apply w-72;
    }

    &.ds-dropdown-xxl {
      @apply w-96;
    }
  }

  &-item {
    @apply text-gray-500 no-underline hover:no-underline text-sm bg-white hover:bg-gray-50 w-full flex items-end px-4 py-2 font-medium;

    &-active {
      @apply bg-gray-50 no-underline;
    }
  }

  &-enter {
    @apply transition duration-200 ease-out;

    &-from {
      @apply scale-95 opacity-0;
    }

    &-to {
      @apply scale-100 opacity-100;
    }
  }

  &-leave {
    @apply transition ease-in-out duration-150;

    &-from {
      @apply scale-100 opacity-100;
    }

    &-to {
      @apply scale-95 opacity-0;
    }
  }

  &-icon {
    @apply h-5 w-5 mr-4;
  }
}
</style>
